/**
 * Phase 9.1 — Clickable helpers.
 *
 * Small primitives that add drill-down affordances (cursor, hover, tooltip
 * hint) to plain text without changing the surrounding layout. Consumers
 * pass the target id / sku / order and either wire up a modal opener
 * (products, orders) or navigate to a route (employees).
 */
import React from "react";
import { Link } from "react-router-dom";
import { useDrillDown } from "./DrillDownContext";

const baseCls =
  "cursor-pointer underline decoration-transparent underline-offset-2 " +
  "transition-colors duration-100 hover:decoration-current hover:text-teal-300 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:rounded-sm";

/**
 * ClickableEmployee — accepts EITHER `canonicalId` OR `code` (Z18, 004…)
 * OR `name`. When only code/name is given, the DrillDownProvider's
 * cached alias map is used to resolve the canonical_id at click time.
 */
export function ClickableEmployee({
  canonicalId,
  code,
  name,
  children,
  className = "",
  stopPropagation = false,
}) {
  const { resolveCanonicalId } = useDrillDown();
  const resolved = canonicalId || resolveCanonicalId(code) || resolveCanonicalId(name);
  if (!resolved) return <>{children}</>;
  return (
    <Link
      to={`/employees/${resolved}`}
      className={`${baseCls} ${className}`}
      data-testid={`clickable-employee-${resolved}`}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
    >
      {children}
    </Link>
  );
}

export function ClickableSKU({ sku, onOpen, children, className = "", stopPropagation = false }) {
  if (!sku || !onOpen) return <>{children}</>;
  return (
    <button
      type="button"
      className={`${baseCls} ${className}`}
      data-testid={`clickable-sku-${sku}`}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onOpen(sku);
      }}
    >
      {children}
    </button>
  );
}

export function ClickableOrder({ orderId, onOpen, children, className = "", stopPropagation = false }) {
  if (!orderId || !onOpen) return <>{children}</>;
  return (
    <button
      type="button"
      className={`${baseCls} ${className}`}
      data-testid={`clickable-order-${orderId}`}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onOpen(orderId);
      }}
    >
      {children}
    </button>
  );
}
